import { useAuth } from "@/hooks/useAuth";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarInset,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarProvider,
  SidebarTrigger,
  useSidebar,
} from "@/components/ui/sidebar";
import { APP_LOGO, APP_TITLE, getLoginUrl } from "@shared/const";
import SearchBar from "./SearchBar";
import LanguageSwitcher from "./LanguageSwitcher";
import FeedbackModal from "./FeedbackModal";
import { useIsMobile } from "@/hooks/useMobile";
import { BarChart3, BookOpen, Calculator, FileText, Home, LayoutDashboard, LogOut, PanelLeft, Settings, Shield, Users, Building2, GraduationCap, Info, HelpCircle, Ship, TrendingUp, MessageSquare, Table } from "lucide-react";
import { CSSProperties, useEffect, useRef, useState } from "react";
import { useLocation } from "wouter";
import { DashboardLayoutSkeleton } from './DashboardLayoutSkeleton';
import { Button } from "./ui/button";
import { useTranslation } from 'react-i18next';

const menuItems = [
  { icon: HelpCircle, label: "Guide", path: "/guide", i18nKey: 'nav.guide' },
  { icon: Home, label: "Dashboard", path: "/dashboard", i18nKey: 'nav.dashboard' },
  { icon: Calculator, label: "Calculator", path: "/calculator", i18nKey: 'nav.calculator' },
  { icon: BarChart3, label: "Reports", path: "/reports", i18nKey: 'nav.reports' },
  { icon: TrendingUp, label: "Benchmark", path: "/benchmark", i18nKey: 'nav.benchmark' },
  { icon: Ship, label: "CBAM", path: "/cbam", i18nKey: 'nav.cbam' },
  { icon: BookOpen, label: "Learn", path: "/learn", i18nKey: 'nav.learn' },
  { icon: Settings, label: "Settings", path: "/settings", i18nKey: 'nav.settings' },
  { icon: Info, label: "Account Status", path: "/system-status", i18nKey: 'nav.accountStatus' },
];

// Admin-only menu items
const adminMenuItems = [
  { icon: Shield, label: "Admin Learn", path: "/admin/learn", i18nKey: 'nav.adminLearn' },
  { icon: GraduationCap, label: "Learning", path: "/admin/learning", i18nKey: 'nav.learning' },
  { icon: Users, label: "Users", path: "/admin/users", i18nKey: 'nav.users' },
  { icon: Building2, label: "Companies", path: "/admin/companies", i18nKey: 'nav.companies' },
  { icon: BarChart3, label: "Client Emissions", path: "/admin/emissions", i18nKey: 'nav.clientEmissions' },
  { icon: FileText, label: "Reference Files", path: "/admin/files", i18nKey: 'nav.referenceFiles' },
  { icon: Table, label: "Edit Reference Tables", path: "/admin/reference-tables", i18nKey: 'nav.editReferenceTables' },
  { icon: MessageSquare, label: "Feedback", path: "/admin/feedback", i18nKey: 'nav.feedback' },
];

// Sign Out Button Component
function SignOutButton({ onClick }: { onClick: () => void }) {
  const { t } = useTranslation();
  return (
    <DropdownMenuItem
      onClick={onClick}
      className="cursor-pointer text-destructive focus:text-destructive"
    >
      <LogOut className="mr-2 h-4 w-4" />
      <span>{t('nav.logout')}</span>
    </DropdownMenuItem>
  );
}

// Admin Section Title Component
function AdminSectionTitle() {
  const { t } = useTranslation();
  return (
    <div className="px-4 py-2 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
      {t('nav.admin')}
    </div>
  );
}

// Translated MenuItem Component
function TranslatedMenuItem({ item, isActive, onClick }: {
  item: typeof menuItems[0];
  isActive: boolean;
  onClick: () => void;
}) {
  const { t } = useTranslation();
  const label = item.i18nKey ? t(item.i18nKey) : item.label;
  
  return (
    <SidebarMenuButton
      isActive={isActive}
      onClick={onClick}
      tooltip={label}
      className="h-10 transition-all font-normal"
    >
      <item.icon className={`h-4 w-4 ${isActive ? "text-primary" : ""}`} />
      <span>{label}</span>
    </SidebarMenuButton>
  );
}

const SIDEBAR_WIDTH_KEY = "sidebar-width";
const DEFAULT_WIDTH = 280;
const MIN_WIDTH = 200;
const MAX_WIDTH = 480;

export default function DashboardLayout({
  children,
}: {
  children: React.ReactNode;
}) {
  const [sidebarWidth, setSidebarWidth] = useState(() => {
    const saved = localStorage.getItem(SIDEBAR_WIDTH_KEY);
    return saved ? parseInt(saved, 10) : DEFAULT_WIDTH;
  });
  const { loading, user } = useAuth();
  const [loadingTimeout, setLoadingTimeout] = useState(false);

  useEffect(() => {
    localStorage.setItem(SIDEBAR_WIDTH_KEY, sidebarWidth.toString());
  }, [sidebarWidth]);

  // Add timeout to prevent infinite skeleton
  useEffect(() => {
    if (loading) {
      const timer = setTimeout(() => {
        console.warn('[DashboardLayout] Loading timeout - redirecting to home');
        setLoadingTimeout(true);
        window.location.href = '/';
      }, 10000); // 10 seconds timeout

      return () => clearTimeout(timer);
    }
  }, [loading]);

  if (loading && !loadingTimeout) {
    return <DashboardLayoutSkeleton />
  }

  if (!user) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="flex flex-col items-center gap-8 p-8 max-w-md w-full">
          <div className="flex flex-col items-center gap-6">
            <div className="relative group">
              <div className="relative">
                <img
                  src={APP_LOGO}
                  alt={APP_TITLE}
                  className="h-20 w-20 rounded-xl object-cover shadow"
                />
              </div>
            </div>
            <div className="text-center space-y-2">
              <h1 className="text-2xl font-bold tracking-tight">{APP_TITLE}</h1>
              <p className="text-sm text-muted-foreground">
                Please sign in to continue
              </p>
            </div>
          </div>
          <Button
            onClick={() => {
              window.location.href = getLoginUrl();
            }}
            size="lg"
            className="w-full shadow-lg hover:shadow-xl transition-all"
          >
            Sign in
          </Button>
        </div>
      </div>
    );
  }

  return (
    <SidebarProvider
      style={
        {
          "--sidebar-width": `${sidebarWidth}px`,
        } as CSSProperties
      }
    >
      <DashboardLayoutContent setSidebarWidth={setSidebarWidth}>
        {children}
      </DashboardLayoutContent>
    </SidebarProvider>
  );
}

type DashboardLayoutContentProps = {
  children: React.ReactNode;
  setSidebarWidth: (width: number) => void;
};

function DashboardLayoutContent({
  children,
  setSidebarWidth,
}: DashboardLayoutContentProps) {
  const { t } = useTranslation();
  const { user, signOut } = useAuth();
  const [location, setLocation] = useLocation();
  const { state, toggleSidebar } = useSidebar();
  const isCollapsed = state === "collapsed";
  const [isResizing, setIsResizing] = useState(false);
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const sidebarRef = useRef<HTMLDivElement>(null);
  const activeMenuItem = menuItems.find(item => item.path === location);
  const isMobile = useIsMobile();

  useEffect(() => {
    if (isCollapsed) {
      setIsResizing(false);
    }
  }, [isCollapsed]);

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isResizing) return;

      const sidebarLeft = sidebarRef.current?.getBoundingClientRect().left ?? 0;
      const newWidth = e.clientX - sidebarLeft;
      if (newWidth >= MIN_WIDTH && newWidth <= MAX_WIDTH) {
        setSidebarWidth(newWidth);
      }
    };

    const handleMouseUp = () => {
      setIsResizing(false);
    };

    if (isResizing) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
      document.body.style.cursor = "col-resize";
      document.body.style.userSelect = "none";
    }

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    };
  }, [isResizing, setSidebarWidth]);

  return (
    <>
      <div className="relative" ref={sidebarRef}>
        <Sidebar
          collapsible="icon"
          className="border-r-0"
          disableTransition={isResizing}
        >
          <SidebarHeader className="h-16 justify-center">
            <div className="flex items-center gap-3 pl-2 group-data-[collapsible=icon]:px-0 transition-all w-full">
              {isCollapsed ? (
                <div className="relative h-8 w-8 shrink-0 group">
                  <img
                    src={APP_LOGO}
                    className="h-8 w-8 rounded-md object-cover ring-1 ring-border"
                    alt="Logo"
                  />
                  <button
                    onClick={toggleSidebar}
                    className="absolute inset-0 flex items-center justify-center bg-accent rounded-md ring-1 ring-border opacity-0 group-hover:opacity-100 transition-opacity focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  >
                    <PanelLeft className="h-4 w-4 text-foreground" />
                  </button>
                </div>
              ) : (
                <>
                  <div className="flex items-center gap-3 min-w-0">
                    <img
                      src={APP_LOGO}
                      className="h-8 w-8 rounded-md object-cover ring-1 ring-border shrink-0"
                      alt="Logo"
                    />
                    <span className="font-semibold tracking-tight truncate">
                      {APP_TITLE}
                    </span>
                  </div>
                  <button
                    onClick={toggleSidebar}
                    className="ml-auto h-8 w-8 flex items-center justify-center hover:bg-accent rounded-lg transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-ring shrink-0"
                  >
                    <PanelLeft className="h-4 w-4 text-muted-foreground" />
                  </button>
                </>
              )}
            </div>
          </SidebarHeader>

          <SidebarContent className="gap-0">
            <SidebarMenu className="px-2 py-1">
              {menuItems.map(item => (
                <SidebarMenuItem key={item.path}>
                  <TranslatedMenuItem
                    item={item}
                    isActive={location === item.path}
                    onClick={() => setLocation(item.path)}
                  />
                </SidebarMenuItem>
              ))}
            </SidebarMenu>

            {/* Admin Menu Section */}
            {(user?.role === 'mypa_super_admin' || user?.role === 'mypa_data_admin') && (
              <>
                <AdminSectionTitle />
                <SidebarMenu className="px-2 py-1">
                  {adminMenuItems.map(item => (
                    <SidebarMenuItem key={item.path}>
                      <TranslatedMenuItem
                        item={item}
                        isActive={location === item.path}
                        onClick={() => setLocation(item.path)}
                      />
                    </SidebarMenuItem>
                  ))}
                </SidebarMenu>
              </>
            )}
          </SidebarContent>

          <SidebarFooter className="p-3">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="flex items-center gap-3 rounded-lg px-1 py-1 hover:bg-accent/50 transition-colors w-full text-left group-data-[collapsible=icon]:justify-center focus:outline-none focus-visible:ring-2 focus-visible:ring-ring">
                  <Avatar className="h-9 w-9 border shrink-0">
                    <AvatarFallback className="text-xs font-medium">
                      {(user?.firstName || user?.email)?.charAt(0).toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1 min-w-0 group-data-[collapsible=icon]:hidden">
                    <p className="text-sm font-medium truncate leading-none">
                      {user?.firstName || user?.email || "-"}
                    </p>
                    <p className="text-xs text-muted-foreground truncate mt-1.5">
                      {user?.email || "-"}
                    </p>
                  </div>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <SignOutButton onClick={signOut} />
              </DropdownMenuContent>
            </DropdownMenu>
          </SidebarFooter>
        </Sidebar>
        <div
          className={`absolute top-0 right-0 w-1 h-full cursor-col-resize hover:bg-primary/20 transition-colors ${isCollapsed ? "hidden" : ""}`}
          onMouseDown={() => {
            if (isCollapsed) return;
            setIsResizing(true);
          }}
          style={{ zIndex: 50 }}
        />
      </div>

      <SidebarInset>
        {isMobile && (
          <div className="flex border-b h-14 items-center justify-between bg-background/95 px-2 backdrop-blur supports-[backdrop-filter]:backdrop-blur sticky top-0 z-40">
            <div className="flex items-center gap-2 flex-1">
              <SidebarTrigger className="h-9 w-9 rounded-lg bg-background" />
              <div className="flex items-center gap-3 flex-1">
                <div className="flex flex-col gap-1">
                  <span className="tracking-tight text-foreground">
                    {activeMenuItem?.label ?? APP_TITLE}
                  </span>
                </div>
                <div className="ml-auto flex items-center gap-2">
                  <div className="w-64">
                    <SearchBar />
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setFeedbackOpen(true)}
                    className="gap-2"
                  >
                    <MessageSquare className="h-4 w-4" />
                    <span className="hidden sm:inline">{t('feedback.button')}</span>
                  </Button>
                  <LanguageSwitcher />
                </div>
              </div>
            </div>
          </div>
        )}
        {!isMobile && (
          <div className="flex border-b h-14 items-center justify-end gap-3 bg-background/95 px-4 backdrop-blur supports-[backdrop-filter]:backdrop-blur sticky top-0 z-40">
            <div className="w-96">
              <SearchBar />
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setFeedbackOpen(true)}
              className="gap-2"
            >
              <MessageSquare className="h-4 w-4" />
              <span className="hidden sm:inline">{t('feedback.button')}</span>
            </Button>
            <LanguageSwitcher />
          </div>
        )}
        <main className="flex-1 p-4">{children}</main>
      </SidebarInset>
      <FeedbackModal open={feedbackOpen} onOpenChange={setFeedbackOpen} />
    </>
  );
}
